/**
 * Search History Service - Manages user search history and saved searches
 * Provides functionality for tracking, retrieving, and managing search history
 */

const SEARCH_HISTORY_KEY = 'recrutees_search_history';
const SAVED_SEARCHES_KEY = 'recrutees_saved_searches';
const MAX_HISTORY_ITEMS = 50;

/**
 * Adds a search to history
 * @param {string} query - Search query
 * @param {Object} filters - Applied filters
 * @param {number} resultCount - Number of results
 */
export function addToSearchHistory(query, filters = {}, resultCount = 0) {
  try {
    if (!query?.trim()) return;

    const history = getSearchHistory();
    
    const searchEntry = {
      id: Date.now()?.toString(),
      query: query?.trim(),
      filters,
      resultCount,
      timestamp: new Date()?.toISOString()
    };

    // Remove duplicate if exists
    const filtered = history?.filter(item => 
      item?.query?.toLowerCase() !== query?.trim()?.toLowerCase()
    );

    // Add to beginning
    filtered?.unshift(searchEntry);

    // Limit history size
    const limited = filtered?.slice(0, MAX_HISTORY_ITEMS);

    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(limited));
  } catch (error) {
    console.error('Error adding to search history:', error);
  }
}

/**
 * Gets search history
 * @param {number} limit - Maximum number of items to return
 * @returns {Array} Search history items
 */
export function getSearchHistory(limit = 20) {
  try {
    const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
    if (!stored) return [];

    const history = JSON.parse(stored);
    return Array.isArray(history) ? history?.slice(0, limit) : [];
  } catch (error) {
    console.error('Error getting search history:', error);
    return [];
  }
}

/**
 * Clears search history
 */
export function clearSearchHistory() {
  try {
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  } catch (error) {
    console.error('Error clearing search history:', error);
  }
}

/**
 * Removes a specific search from history
 * @param {string} id - Search entry ID
 */
export function removeFromSearchHistory(id) {
  try {
    const history = getSearchHistory(MAX_HISTORY_ITEMS);
    const filtered = history?.filter(item => item?.id !== id);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error removing from search history:', error);
  }
}

/**
 * Saves a search for quick access
 * @param {string} name - Search name
 * @param {string} query - Search query
 * @param {Object} filters - Search filters
 * @returns {Object} Saved search entry
 */
export function saveSearch(name, query, filters = {}) {
  try {
    if (!name?.trim() || !query?.trim()) {
      throw new Error('Name and query are required');
    }

    const savedSearches = getSavedSearches();
    
    const searchEntry = {
      id: Date.now()?.toString(),
      name: name?.trim(),
      query: query?.trim(),
      filters,
      createdAt: new Date()?.toISOString(),
      lastUsed: new Date()?.toISOString()
    };

    savedSearches?.unshift(searchEntry);
    localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(savedSearches));

    return searchEntry;
  } catch (error) {
    console.error('Error saving search:', error);
    throw error;
  }
}

/**
 * Gets saved searches
 * @returns {Array} Saved searches
 */
export function getSavedSearches() {
  try {
    const stored = localStorage.getItem(SAVED_SEARCHES_KEY);
    if (!stored) return [];

    const searches = JSON.parse(stored);
    return Array.isArray(searches) ? searches : [];
  } catch (error) {
    console.error('Error getting saved searches:', error);
    return [];
  }
}

/**
 * Deletes a saved search
 * @param {string} id - Search ID
 */
export function deleteSavedSearch(id) {
  try {
    const searches = getSavedSearches();
    const filtered = searches?.filter(item => item?.id !== id);
    localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error deleting saved search:', error);
  }
}

/**
 * Updates last used timestamp for a saved search
 * @param {string} id - Search ID
 */
export function updateSavedSearchUsage(id) {
  try {
    const searches = getSavedSearches();
    const updated = searches?.map(search => 
      search?.id === id 
        ? { ...search, lastUsed: new Date()?.toISOString() }
        : search
    );
    localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Error updating saved search:', error);
  }
}

/**
 * Gets popular searches based on frequency
 * @param {number} limit - Number of popular searches to return
 * @returns {Array} Popular search queries
 */
export function getPopularSearches(limit = 5) {
  try {
    const history = getSearchHistory(MAX_HISTORY_ITEMS);
    
    // Count query frequency
    const frequency = {};
    history?.forEach(item => {
      const query = item?.query?.toLowerCase();
      frequency[query] = (frequency?.[query] || 0) + 1;
    });

    // Sort by frequency
    const popular = Object.entries(frequency)?.sort((a, b) => b?.[1] - a?.[1])?.slice(0, limit)?.map(([query]) => query);

    return popular;
  } catch (error) {
    console.error('Error getting popular searches:', error);
    return [];
  }
}